import React, { useCallback, useState, useEffect } from "react";
import PropTypes from "prop-types";
import { styled } from "@mui/material/styles";

import isNil from "lodash/isNil";
import TextField from "@mui/material/TextField";

const NAN_VALUE_ERROR =
  "You must enter a number value for numerical type attributes";
const INVERTED_RANGE_ERROR = "'To' must be larger than 'From'";

export const NumericField = ({
  attributeName,
  fromValue,
  toValue,
  onChangeFromValue,
  onChangeToValue,
  onChangeBothValue
}) => {
  const [fromInput, setFromInput] = useState(fromValue ?? "");
  const [toInput, setToInput] = useState(toValue ?? "");
  const [fromInputError, setFromInputError] = useState("");
  const [toInputError, setToInputError] = useState("");

  // From input validation update
  const handleFromData = useCallback(
    value => {
      setFromInput(value);

      const isNumericalValueNaN = Number.isNaN(Number(value));
      const isNumericalToValueNaN = Number.isNaN(Number(toInput));
      const isReversed = !!toValue && parseInt(value) > parseInt(toValue);
      const isReversedInput =
        toInput !== "" && parseInt(value) > parseInt(toInput);

      if (!isNil(value) && isNumericalValueNaN) {
        setFromInputError(NAN_VALUE_ERROR);
        onChangeFromValue("");
      } else if (isReversed) {
        setFromInputError(INVERTED_RANGE_ERROR);
        onChangeFromValue("");
      } else if (
        toInput === "" ||
        (toInput !== "" && !isNumericalToValueNaN && !isReversedInput)
      ) {
        // If toInput is empty, remove error bypass or check validation
        // In case of From-Value passed validation, but also it needs to be reflected update to To-Value
        setFromInputError("");
        setToInputError("");
        onChangeBothValue(value, toInput);
      } else {
        setFromInputError("");
        onChangeFromValue(value);
      }
    },
    [toInput, toValue, onChangeFromValue, onChangeBothValue]
  );

  // To input validation update
  const handleToData = useCallback(
    value => {
      setToInput(value);

      const isNumericalValueNaN = Number.isNaN(Number(value));
      const isNumericalFromValueNaN = Number.isNaN(Number(fromInput));
      const isReversed = !!fromValue && parseInt(fromValue) > parseInt(value);
      const isReversedInput =
        fromInput !== "" && parseInt(fromInput) > parseInt(value);

      if (!isNil(value) && isNumericalValueNaN) {
        setToInputError(NAN_VALUE_ERROR);
        onChangeToValue("");
      } else if (isReversed) {
        setToInputError(INVERTED_RANGE_ERROR);
        onChangeToValue("");
      } else if (
        fromInput === "" ||
        (fromInput !== "" && !isNumericalFromValueNaN && !isReversedInput)
      ) {
        // If fromInput is empty, remove error bypass or check validation
        // In case of To-Value passed validation, but also it needs to be reflected update to From-Value
        setFromInputError("");
        setToInputError("");
        onChangeBothValue(fromInput, value);
      } else {
        setToInputError("");
        onChangeToValue(value);
      }
    },
    [fromInput, fromValue, onChangeBothValue, onChangeToValue]
  );

  // When filter cleared without error, clear input field also
  useEffect(() => {
    if (!fromValue && !fromInputError) setFromInput("");
  }, [fromInputError, fromValue]);
  useEffect(() => {
    if (!toValue && !toInputError) setToInput("");
  }, [toInputError, toValue]);

  return (
    <RowFields>
      {/* From */}
      <StyledTextField
        variant="outlined"
        margin="dense"
        aria-label="meta value input"
        error={Boolean(fromInputError)}
        helperText={fromInputError}
        value={fromInput}
        onChange={evt => handleFromData(evt.target.value)}
        placeholder={`${attributeName} from`}
        size="small"
      />

      {/* To */}
      <StyledTextField
        variant="outlined"
        margin="dense"
        aria-label="meta value input"
        error={Boolean(toInputError)}
        helperText={toInputError}
        value={toInput}
        onChange={evt => handleToData(evt.target.value)}
        placeholder={`${attributeName} to`}
        size="small"
      />
    </RowFields>
  );
};

const RowFields = styled("div")`
  display: flex;
  width: 100%;
  gap: 10px;

  & .MuiTextField-root {
    width: 100%;
  }
`;

const StyledTextField = styled(TextField)`
  margin: 0;
`;

NumericField.propTypes = {
  attributeName: PropTypes.string.isRequired,
  fromValue: PropTypes.string,
  toValue: PropTypes.string,
  onChangeFromValue: PropTypes.func.isRequired,
  onChangeToValue: PropTypes.func.isRequired,
  onChangeBothValue: PropTypes.func.isRequired
};
